/**
 * EE-seam widget registration (Decision 0.4-6 option (c), 2026-07-05):
 * generic Anvil widgets are AUTHORED on the CE-shared path `widgets/wds/`
 * but REGISTERED here, keeping the two sync-exposed CE files
 * (`widgets/index.ts`, `widgets/wds/constants.ts`) untouched. Keys follow
 * the WDS_V2_WIDGET_MAP convention: classic widget type → WDS successor
 * where a classic counterpart exists, self-key for net-new widgets.
 * CE mirroring of the Wave-1+ widgets is the Wave-gate-4 GA gate.
 */
export const WDS_EE_WIDGET_MAP = {
  WDS_AI_CHAT_WIDGET: "WDS_AI_CHAT_WIDGET",
  WDS_ANVIL_TABLE_WIDGET: "WDS_ANVIL_TABLE_WIDGET",
  // Wave 1 (2026-07-05)
  IMAGE_WIDGET: "WDS_IMAGE_WIDGET",
  DIVIDER_WIDGET: "WDS_DIVIDER_WIDGET",
  RATE_WIDGET: "WDS_RATE_WIDGET",
  WDS_BADGE_WIDGET: "WDS_BADGE_WIDGET",
  WDS_AVATAR_WIDGET: "WDS_AVATAR_WIDGET",
  WDS_LINK_WIDGET: "WDS_LINK_WIDGET",
  WDS_CODE_WIDGET: "WDS_CODE_WIDGET",
  // Wave 1b (2026-07-05) — primitive-first. Classic keys map the WDS successor
  // (RANGE_SLIDER_WIDGET / CATEGORY_SLIDER_WIDGET also migrate to
  // WDS_SLIDER_WIDGET via its mode discriminator — see the Export migration
  // table in .claude/anvil/widget-feature-parity-audit.md §7).
  PROGRESS_WIDGET: "WDS_PROGRESS_WIDGET",
  NUMBER_SLIDER_WIDGET: "WDS_SLIDER_WIDGET",
  FILE_PICKER_WIDGET_V2: "WDS_FILE_UPLOAD_WIDGET",
  WDS_SEARCH_WIDGET: "WDS_SEARCH_WIDGET",
  WDS_CHIP_GROUP_WIDGET: "WDS_CHIP_GROUP_WIDGET",
  // Wave 2 (2026-07-06) — canvas hosts. Classic key → WDS successor: the
  // palette successor filter replaces classic Tabs in Anvil apps.
  TABS_WIDGET: "WDS_TABS_WIDGET",
  WDS_CARD_WIDGET: "WDS_CARD_WIDGET",
  // Wave 3 (2026-07-06) — net-new, EE-homed next to the AI Chat shell it
  // evolved from (self-key; no classic counterpart). Promotion to the
  // CE-shared path is a wave-gate call — see WDSChatWidget/widget jsdoc.
  WDS_CHAT_WIDGET: "WDS_CHAT_WIDGET",
  // Wave 3 (2026-07-06) — media/geo/tree/overlay. Classic keys map the WDS
  // successor (2→1 collapses are intentional: both classic widgets are
  // replaced by the unified successor in Anvil apps). Self-keys are net-new.
  IFRAME_WIDGET: "WDS_EMBED_WIDGET",
  DOCUMENT_VIEWER_WIDGET: "WDS_EMBED_WIDGET",
  VIDEO_WIDGET: "WDS_MEDIA_WIDGET",
  AUDIO_WIDGET: "WDS_MEDIA_WIDGET",
  MAP_CHART_WIDGET: "WDS_MAP_CHART_WIDGET",
  SINGLE_SELECT_TREE_WIDGET: "WDS_TREE_SELECT_WIDGET",
  MULTI_SELECT_TREE_WIDGET: "WDS_TREE_SELECT_WIDGET",
  WDS_SHEET_WIDGET: "WDS_SHEET_WIDGET",
  WDS_CAROUSEL_WIDGET: "WDS_CAROUSEL_WIDGET",
  WDS_FAB_WIDGET: "WDS_FAB_WIDGET",
  // Anvil row-composition List (A1) — classic List-v1 evaluate-as-array
  // mechanism, now LIVE (both wiring seams landed: WIDGETS_WITH_CHILD_WIDGETS
  // + the syncRowTemplate transform-invocation). Owns "List"; flat WDSListWidget
  // is "Text List". Classic LIST_WIDGET_V2 → this successor.
  LIST_WIDGET_V2: "WDS_LIST_VIEW_WIDGET",
  // M4 Tier 1 (2026-08-18) — net-new, no classic counterpart (classic has no
  // range picker), hence self-keyed. Sibling of WDS_DATEPICKER_WIDGET sharing
  // its parse/serialize helpers; separate widget (not a mode discriminator)
  // per the architect-adjudicated design.
  WDS_DATE_RANGE_PICKER_WIDGET: "WDS_DATE_RANGE_PICKER_WIDGET",
  // M4 Tier 1 (2026-08-18) — net-new, no classic counterpart (classic has no
  // color picker), hence self-keyed. First consumer of the ON_COLOR_CHANGE
  // EventType (sanctioned EE divergence at the ActionConstants enum tail).
  WDS_COLOR_PICKER_WIDGET: "WDS_COLOR_PICKER_WIDGET",
  // M4 Tier 2 (2026-08-19) — classic key → WDS successor: scan-mode /
  // defaultCamera / onCodeDetected semantics ported (reusing the CE-shared
  // EventType.ON_CODE_DETECTED — zero enum divergence); the Blueprint /
  // react-modal / styling-prop baggage was not.
  CODE_SCANNER_WIDGET: "WDS_CODE_SCANNER_WIDGET",
  // M4 (2026-08-19) — net-new DISPLAY tree, self-keyed (classic has no
  // display tree; both classic tree widgets are pickers and map to
  // WDS_TREE_SELECT_WIDGET above). onNodeSelect reuses ON_OPTION_CHANGE;
  // onNodeExpand introduces the EE-tail ON_NODE_EXPAND EventType.
  WDS_TREE_WIDGET: "WDS_TREE_WIDGET",
  // M4 (2026-08-19) — net-new, self-keyed (classic has no signature pad).
  // Introduces the EE-tail ON_SIGNATURE_CHANGE EventType; capture is
  // user-gesture-only (no setValue setter, no default property — see the
  // widget jsdoc's inert-binding-data security contract).
  WDS_SIGNATURE_WIDGET: "WDS_SIGNATURE_WIDGET",
  // M4 Tier 3 (2026-08-20) — classic key → WDS successor. Same vendored
  // TinyMCE 7.9.3 engine, but the oxide chrome is replaced by WDS
  // ToolbarButtons driving execCommand; the classic styling props
  // (borderRadius/boxShadow/labelPosition…) and the 16-plugin surface were
  // deliberately not ported. The entry is what makes the widget Anvil-only
  // (anvilWidgetPaletteIsolation asserts it) and gives Export-to-Anvil its
  // RICH_TEXT_EDITOR_WIDGET mapping.
  RICH_TEXT_EDITOR_WIDGET: "WDS_RICH_TEXT_EDITOR_WIDGET",
};
